<template>
  <div class="plan-step">
    <div class="row g-4">
      <div class="col-lg-5 order-2 order-lg-1">
        <SolderaProofComponent />
      </div>

      <div class="col-lg-7 order-1 order-lg-2">
        <p class="step-eyebrow">{{ $t("billing.plan.eyebrow") }}</p>
        <h2 class="plan-title font-serif mb-3">
          {{ $t("billing.plan.title") }}
        </h2>

        <div v-if="status.reporting_standards.length" class="mb-3">
          <span class="small text-body-secondary me-2">
            {{ $t("billing.plan.tailored_for") }}
          </span>
          <span
            v-for="standard in status.reporting_standards"
            :key="standard"
            class="standard-pill me-1 mb-1"
          >
            {{
              standard === "other" && status.reporting_standard_other
                ? status.reporting_standard_other
                : $t(`billing.qualification.standards.${standard}.name`)
            }}
          </span>
        </div>

        <div class="price-card" data-testid="price-card">
          <div
            class="d-flex flex-wrap align-items-baseline justify-content-between gap-2"
          >
            <h3 class="h4 mb-0">{{ planName }}</h3>
            <span
              v-if="status.consumption_band && status.country_scope"
              class="small text-body-secondary"
            >
              {{
                $t("billing.plan.based_on", {
                  consumption: $t(
                    `billing.qualification.consumption_bands.${status.consumption_band}.name`
                  ),
                  countries: $t(
                    `billing.qualification.country_scopes.${status.country_scope}`
                  ),
                })
              }}
            </span>
          </div>
          <div v-if="status.price" class="my-3">
            <span class="price-amount font-serif" data-testid="price-amount">{{
              formattedPrice
            }}</span>
            <span class="price-interval ms-1">{{
              $t("billing.plan.per_year")
            }}</span>
            <div class="small text-body-secondary">
              {{ $t("billing.plan.billed_annually") }} ·
              {{ $t("billing.plan.vat_note") }}
            </div>
          </div>

          <p class="included-label">{{ $t("billing.plan.included_title") }}</p>
          <ul class="list-unstyled mb-4">
            <li
              v-for="item in INCLUDED"
              :key="item"
              class="included-item d-flex align-items-start gap-2"
              :class="{ 'is-highlight': item === 'account_executive' }"
            >
              <FontAwesomeIcon
                :icon="INCLUDED_ICONS[item] ?? 'check'"
                class="included-icon mt-1"
                fixed-width
              />
              <span>{{
                $t(`billing.plan.included.${item}`, {
                  standards: standardsList,
                })
              }}</span>
            </li>
          </ul>

          <div
            class="guarantee d-flex align-items-start gap-2 mb-3"
            data-testid="money-back-guarantee"
          >
            <FontAwesomeIcon
              icon="shield-halved"
              class="guarantee-icon mt-1"
              fixed-width
            />
            <div>
              <div class="fw-semibold">
                {{ $t("billing.plan.guarantee_title") }}
              </div>
              <div class="small text-body-secondary">
                {{ $t("billing.plan.guarantee_body") }}
              </div>
            </div>
          </div>

          <button
            type="button"
            class="btn btn-primary checkout-button w-100"
            :disabled="loading"
            data-testid="checkout-button"
            @click="$emit('checkout')"
          >
            <span
              v-if="loading"
              class="spinner-border spinner-border-sm me-2"
              role="status"
              aria-hidden="true"
            ></span>
            <FontAwesomeIcon v-else icon="lock" class="me-2" />
            {{
              loading ? $t("billing.plan.redirecting") : $t("billing.plan.cta")
            }}
          </button>
          <p class="small text-body-secondary text-center mt-2 mb-0">
            {{ $t("billing.plan.secure_note") }}
          </p>
        </div>

        <div class="d-flex flex-wrap justify-content-between gap-2 small mt-3">
          <button
            type="button"
            class="btn btn-link p-0"
            data-testid="change-answers"
            @click="$emit('back')"
          >
            {{ $t("billing.plan.change_answers") }}
          </button>
          <a
            :href="demoUrl"
            target="_blank"
            rel="noopener noreferrer"
            class="link-secondary"
          >
            {{ $t("billing.plan.talk_to_us") }}
            <FontAwesomeIcon icon="arrow-up-right-from-square" class="ms-1" />
          </a>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { BillingStatus } from "@/billing/billingTypes";
import SolderaProofComponent from "@/billing/SolderaProofComponent.vue";
import { computed } from "vue";
import { useI18n } from "vue-i18n";

// soldera.org's corporate promise in short, led by the people who run it for the customer.
const INCLUDED = [
  "account_executive",
  "call_plan",
  "matched",
  "buy_missing",
  "evidence",
  "suppliers",
] as const;

const INCLUDED_ICONS: Partial<Record<(typeof INCLUDED)[number], string>> = {
  account_executive: "user-tie",
  call_plan: "calendar-check",
};

const props = withDefaults(
  defineProps<{
    status: BillingStatus;
    /** The demo booking page, with the role preselected. */
    demoUrl: string;
    loading?: boolean;
  }>(),
  { loading: false }
);

defineEmits<{
  checkout: [];
  back: [];
}>();

const { t, locale } = useI18n();

// The standards the customer reports under, as it named them; the common ones when it picked none.
const standardsList = computed(() => {
  const other = props.status.reporting_standard_other;
  const names = props.status.reporting_standards
    .filter((standard) => standard !== "other" || other)
    .map((standard) =>
      standard === "other" && other
        ? other
        : t(`billing.qualification.standards.${standard}.name`)
    );
  return names.length
    ? new Intl.ListFormat(locale.value, { type: "conjunction" }).format(names)
    : t("billing.plan.default_standards");
});

const planName = computed(() =>
  props.status.plan ? t(`billing.plan.names.${props.status.plan}`) : ""
);

// In the page's language, not the browser's: "€2,000" reads as two euros in Estonian, Latvian, Lithuanian and Swedish.
const formattedPrice = computed(() =>
  props.status.price
    ? new Intl.NumberFormat(locale.value, {
        style: "currency",
        currency: "EUR",
        maximumFractionDigits: 0,
      }).format(Number(props.status.price.amount_eur))
    : ""
);
</script>

<style scoped lang="scss">
.step-eyebrow {
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--phoenix-primary);
  margin-bottom: 0.5rem;
}

.plan-title {
  font-size: 2rem;
  font-weight: 600;
  line-height: 1.15;
  letter-spacing: -0.01em;
}

.standard-pill {
  display: inline-block;
  padding: 0.15rem 0.65rem;
  border-radius: 999px;
  font-size: 0.8rem;
  font-weight: 600;
  background: #e8efe2;
  border: 1px solid #b8d0bf;
  color: #1a2820;
}

.price-card {
  border-radius: 12px;
  padding: 1.75rem;
  border: 1px solid #e2e4e2;
  background: #ffffff;
}

.price-amount {
  font-size: 2.75rem;
  font-weight: 600;
  line-height: 1;
  color: #1a2820;
}

.price-interval {
  color: var(--phoenix-secondary-color);
}

.included-label {
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--phoenix-secondary-color);
  margin-bottom: 0.75rem;
}

.included-item {
  margin-bottom: 0.6rem;

  &.is-highlight {
    padding: 0.75rem 0.9rem;
    border-radius: 6px;
    background: #faecca;
    font-weight: 600;
    color: #5d3f06;

    .included-icon {
      color: #c08e3a;
    }
  }
}

.included-icon {
  color: var(--phoenix-primary);
}

.guarantee {
  padding: 0.75rem 0.9rem;
  border-radius: 6px;
  border: 1px solid #b8d0bf;
  background: #eef4ef;
}

.guarantee-icon {
  color: var(--phoenix-primary);
}

.checkout-button {
  min-height: 56px;
  border-radius: 6px;
  font-weight: 600;
}
</style>
